import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Table, Tabs, Space, Tag, Alert, Empty, Spin,
  Tooltip, Button, Segmented,
} from 'antd';
import { ReloadOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';
import GpControl from './cashflow/GpControl';
import BsMovements from './cashflow/BsMovements';
import Statements from './cashflow/Statements';
import {
  INK, NEUTRAL, fmtMoney, moneyCell, FRAME, frameColumns,
} from './cashflow/shared';

/**
 * Cashflow Control.
 *
 * Beside Opex Control rather than inside it: one governs what may be spent, the
 * other when the money moves. ShineHub and PW only, as scoped.
 *
 * Four tabs, and each one answers a question the others do not. GP Control is
 * what is earned and what it costs. Opex is what is spent against a budget.
 * B/S Movements is the cash that never reaches the P&L at all — BAS, loans,
 * assets — which has no budget to be compared against and so had no business
 * sharing a table with opex. Statements rolls all three together.
 *
 * Every tab uses the same seven months and the same eight columns, and each
 * fetches its own data when opened: the opex read walks every payment in the
 * window, and there is no reason to wait for it to look at gross profit.
 */

export default function CashflowControl() {
  // One entity at a time. Stacking both made a page that had to be scrolled
  // past one company to reach the other, and nothing on it compares the two.
  const [entity, setEntity] = useState('shinehub');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('gp');

  /**
   * Three months back, three forward, and the month in between.
   *
   * Fixed rather than chosen. The horizon selector offered six and twelve month
   * views of a forecast that is built three months at a time, which promised a
   * precision the drivers behind it do not have.
   */
  const range = useMemo(() => ({
    from: dayjs().subtract(3, 'month').format('YYYY-MM'),
    to: dayjs().add(3, 'month').format('YYYY-MM'),
    thisMonth: dayjs().format('YYYY-MM-01'),
  }), []);

  /**
   * The opex read only. The other two tabs fetch their own, so opening the page
   * on GP Control does not first wait on every payment in the window.
   */
  const load = useCallback(async (fresh) => {
    if (tab !== 'opex') return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/cashflow/opex', {
        // Opening the tab may serve a few-minute-old read; pressing refresh
        // never does.
        params: { from: range.from, to: range.to, entities: entity, refresh: fresh ? 1 : undefined },
        timeout: 180000,
      });
      setData(r.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the cashflow');
      setData(null);
    }
    setLoading(false);
  }, [range, entity, tab]);

  useEffect(() => { load(); }, [load]);

  const opexTab = (() => {
    if (error) return <Alert type="error" showIcon message={error} />;
    if (!data) {
      return (
        <Card size="small">
          <Empty description={loading ? 'Reading budgets and payments…' : 'No data'} />
        </Card>
      );
    }
    if (!data.entities.length) {
      return (
        <Card size="small">
          <Empty description="You do not have access to this entity." />
        </Card>
      );
    }

    /**
     * One row per account, one column per month — not a row per account-month.
     *
     * The long form made the reader do the pivot: to see whether an account is
     * drifting you had to find its six or seven rows among two hundred and put
     * them side by side. Reading across a row is the whole question this page
     * exists to answer.
     *
     * The columns are not all the same kind of number, which is the point.
     * Three months of what was actually paid, then this month so far, then
     * budget for this month and the three ahead. Actual and budget are kept
     * visually apart because comparing them is the reason to look, and a reader
     * who mistakes one for the other has been misled by the layout.
     */
    const monthAt = (i) => (data.months[i] || null);
    const cols = (pick, opts) => frameColumns(data.months, pick, opts);

    /**
     * The tables stay on screen under a mask while a new entity loads.
     *
     * Switching from ShineHub to PW left the previous entity's numbers sitting
     * there, unlabelled and unchanged, for as long as the read took — only the
     * refresh button showed anything was happening, and it is at the top of the
     * page rather than over the figures being replaced.
     */
    return (
      <Spin spinning={loading} tip="Reading budgets and payments…" size="large">
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {data.entities.map((e) => {
          // account -> { meta, byMonth: { [month]: row } }
          const byAccount = new Map();
          for (const r of e.rows) {
            const key = r.accountCode || r.accountName || 'none';
            const hit = byAccount.get(key) || {
              key,
              accountCode: r.accountCode,
              accountName: r.accountName,
              gstPct: r.gstPct,
              gstSource: r.gstSource,
              byMonth: {},
            };
            hit.byMonth[r.month] = r;
            byAccount.set(key, hit);
          }
          const plRows = [...byAccount.values()].sort((a, b) => {
            const sum = (x) => Object.values(x.byMonth)
              .reduce((t, r) => t + r.budgetInclGst + r.paid, 0);
            return sum(b) - sum(a);
          });

          const accountColumn = {
            title: 'Account',
            dataIndex: 'accountName',
            width: 230,
            fixed: 'left',
            render: (v, r) => (
              <div>
                <div style={{ color: INK }}>{v || r.accountCode || 'Not coded'}</div>
                <div style={{ fontSize: 11, color: NEUTRAL }}>
                  {r.accountCode || '—'}
                  {r.gstPct === 0 && <Tag color="blue" style={{ marginLeft: 6 }}>no GST</Tag>}
                  {r.gstSource === 'default' && r.accountCode && (
                    <Tooltip title="No tax rate on the account and no override, so the global default was used.">
                      <Tag color="orange" style={{ marginLeft: 6 }}>assumed {r.gstPct}%</Tag>
                    </Tooltip>
                  )}
                </div>
              </div>
            ),
          };

          const plColumns = [
            accountColumn,
            ...cols((row, m, kind) => {
              const cell = m ? row.byMonth[m] : null;
              if (!cell) return moneyCell(0);
              if (kind === 'budget') return moneyCell(cell.budgetInclGst, '#8a6d1f');
              return moneyCell(cell.paid);
            }),
          ];

          return (
            <Card
              key={e.entity}
              size="small"
              title={(
                <Space size="small" wrap>
                  <span style={{ fontSize: 12, color: NEUTRAL }}>Budget month</span>
                  {e.months.map((m) => (
                    <Tooltip
                      key={m.month}
                      title={m.locked
                        ? 'That budget month is locked in Opex Control'
                        : 'That budget month is still open in Opex Control — the figure can still change'}
                    >
                      <Tag
                        color={m.locked ? 'default' : 'orange'}
                        icon={m.locked ? <LockOutlined /> : <UnlockOutlined />}
                        style={{ margin: 0 }}
                      >
                        {dayjs(m.month).format('MMM')}
                      </Tag>
                    </Tooltip>
                  ))}
                </Space>
              )}
            >
              {e.paidError && (
                <Alert
                  type="warning" showIcon style={{ marginBottom: 10 }}
                  message={(
                    <span style={{ fontSize: 12 }}>
                      Payments unavailable — {e.paidError}. Budget figures are unaffected; the
                      actual columns are empty rather than zero.
                    </span>
                  )}
                />
              )}
              {(e.paidUndatedBills > 0 || e.paidTruncated) && (
                <Alert
                  type="warning" showIcon style={{ marginBottom: 10 }}
                  message={(
                    <span style={{ fontSize: 12 }}>
                      {e.paidUndatedBills > 0 && (
                        <>
                          {e.paidUndatedBills} bill{e.paidUndatedBills === 1 ? '' : 's'} show money paid
                          but returned no payment dates, so {fmtMoney(e.paidUndated)} could not be placed
                          in a month.{' '}
                        </>
                      )}
                      {e.paidTruncated && 'The payment read hit its page limit, so older settlements may be missing. '}
                      The actual columns are an understatement by that much.
                    </span>
                  )}
                />
              )}
              {e.unknownTaxAccounts.length > 0 && (
                <Alert
                  type="info" showIcon style={{ marginBottom: 10 }}
                  message={(
                    <span style={{ fontSize: 12 }}>
                      {e.unknownTaxAccounts.length} account
                      {e.unknownTaxAccounts.length === 1 ? '' : 's'} had no tax rate in Xero and no
                      override, so {data.gstDefaultPct}% was assumed:{' '}
                      {e.unknownTaxAccounts.slice(0, 6).map((a) => a.name).join(', ')}
                      {e.unknownTaxAccounts.length > 6 ? ' …' : ''}
                    </span>
                  )}
                />
              )}

              <div style={{ fontWeight: 600, fontSize: 13, margin: '4px 0 8px' }}>
                P&amp;L expenses
                <span style={{ fontWeight: 400, color: NEUTRAL, fontSize: 11, marginLeft: 8 }}>
                  actual is cash paid; budget is the Opex Control figure grossed up to what has to
                  leave the bank
                </span>
              </div>
              <Table
                size="small"
                rowKey="key"
                dataSource={plRows}
                columns={plColumns}
                pagination={{ defaultPageSize: 25, showSizeChanger: true }}
                scroll={{ x: 230 + 8 * 118 }}
                locale={{ emptyText: 'No budget, commitments or payments in this window' }}
                summary={(pageRows) => (
                  <Table.Summary.Row style={{ background: '#fafcfd' }}>
                    <Table.Summary.Cell index={0}><b>This page</b></Table.Summary.Cell>
                    {FRAME.map((f, i) => {
                      const m = monthAt(f.idx);
                      const total = pageRows.reduce((t, row) => {
                        const cell = m ? row.byMonth[m] : null;
                        if (!cell) return t;
                        return t + (f.kind === 'budget' ? cell.budgetInclGst : cell.paid);
                      }, 0);
                      return (
                        <Table.Summary.Cell key={f.key} index={i + 1} align="right">
                          <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(total)}</b>
                        </Table.Summary.Cell>
                      );
                    })}
                  </Table.Summary.Row>
                )}
              />

            </Card>
            );
          })}
        </Space>
      </Spin>
    );
  })();

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Cashflow Control</h1>
        <p>ShineHub and PW Renewable — what is budgeted, what is committed, and what has moved, across the P&amp;L and the balance sheet</p>
      </div>

      <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: '10px 14px' } }}>
        <Space wrap size="middle" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space wrap size="middle">
            <Segmented
              value={entity}
              onChange={setEntity}
              options={[
                { label: 'ShineHub', value: 'shinehub' },
                { label: 'PW Renewable', value: 'pw' },
              ]}
            />
            <span style={{ fontSize: 12, color: NEUTRAL }}>
              {dayjs(range.from).format('MMM YYYY')} – {dayjs(range.to).format('MMM YYYY')}
              {' · three months back, this month, three forward'}
            </span>
          </Space>
          {tab === 'opex' && (
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => load(true)}>
              refresh
            </Button>
          )}
        </Space>
      </Card>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          { key: 'gp', label: 'GP Control', children: <GpControl entity={entity} /> },
          { key: 'opex', label: 'Opex', children: opexTab },
          { key: 'bs', label: 'B/S Movements', children: <BsMovements entity={entity} /> },
          { key: 'statements', label: 'Statements', children: <Statements entity={entity} /> },
        ]}
      />
    </PageLayout>
  );
}
